import { Component, OnInit } from '@angular/core';
import {PlatformQuery} from "@shared/models/platform-query/platform-query";
import {takeUntil, take, map, startWith} from "rxjs/operators";
import {PlatformQueryService} from "@core/services/platform-query.service";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {MatCheckboxChange} from "@angular/material/checkbox";
import { IDialogView } from '@shared/components/ok-cancel-dialog/ok-cancel-dialog.component';
import { combineLatest, concat, merge, Observable } from 'rxjs';
import { FuseSplashScreenService } from '@barebone-fuse/@fuse/services/splash-screen.service';
import { DialogModel } from '@shared/models/dialog.model';
import { PlatformCommandResponse } from '@shared/commands/command-base';

@Component({
    standalone: false,
    selector: 'app-manage-stage-area-dialog',
  templateUrl: './manage-stage-area-dialog.component.html',
  styleUrls: ['./manage-stage-area-dialog.component.scss']
})
export class ManageStageAreaDialogComponent implements OnInit, IDialogView {
  public sourceTypeGroup: any;
  public stageAreas: any[];
  public manageStageAreaForm: FormGroup;
  data: DialogModel;
  value: string;
  showSourceMenu = true;
  referenceTypes = [];
  disableAddReference = true;
  possibleNewReference: boolean;
  filteredOptions: any[];
  api = "SpreadsheetApi";
  selectedKeys: string[];
  workbookId: string;
  worksheetId: any;
  fieldKeys: { [name: string]: string } = {};

  constructor(private platformQueryService: PlatformQueryService,
    private fuseSplashScreenService: FuseSplashScreenService,
    private formBuilder: FormBuilder) { }

  ngOnInit(): void {
    this.workbookId = this.data.input.workbookId;
    this.worksheetId = this.data.input.worksheetId;
    this.manageStageAreaForm = this.formBuilder.group({
      sourceType: ['', Validators.required],
      referenceType: ['', Validators.required]
    });
    this.manageStageAreaForm.valueChanges.subscribe(formValue=>{
      this.onFormValuesChanged();
    });

    this.getStageAreas();
    this.manageStageAreaForm.controls['referenceType'].valueChanges.subscribe(value => {
      this.searchReferenceList(value);
      this.disableAddReference = value.length === 0;
    });
  }
  onFormValuesChanged() {
    const referenceName = this.manageStageAreaForm.controls['referenceType'].value;
    this.data.isDisabledOkButton = !(this.manageStageAreaForm.valid && this.referenceTypes.some(o => o === referenceName));
  }

  // Stage area Types
  getStageAreas(sourceId?: string) {
    const query = new PlatformQuery();
    query.name = 'ManageStageAreaQuery';
    query.api = this.api;
    query.isCamelCase = true;
    this.fuseSplashScreenService.show();
    query.fieldValues['WorkbookId'] = this.workbookId;
    query.fieldValues['WorksheetId'] = this.worksheetId;
    query.fieldValues['SelectedSourceMappingStageId'] = sourceId;
    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response => {
      //Selected keys
      this.selectedKeys = response.fieldValues['SelectedKeys'];
      // SourceMappingStageId
      if(!this.stageAreas) {
        this.stageAreas = response.fieldValues['StageAreas'];
      }

      const stageId = response.fieldValues['SelectedSourceMappingStageId'];
      this.sourceTypeGroup = this.stageAreas.find(o => o.id === stageId);
      if(this.sourceTypeGroup) {
        this.manageStageAreaForm.controls['sourceType'].setValue(this.sourceTypeGroup.id);
        this.getSourceTypeDetails(this.sourceTypeGroup);
      }

      // Reference Type
      const referenceTypes = response.fieldValues['ReferenceTypes'];
      this.referenceTypes = referenceTypes;
      const selectedReference = response.fieldValues['SelectedReferenceType'];
      this.manageStageAreaForm.get('referenceType').setValue(selectedReference);
      this.searchReferenceList(selectedReference);
    });
  }

  onSourceChange(event: any) {
    this.getStageAreas(event.value);
  }

  getSelectedKeys(sourceTypeGroup: any) {
    const query = new PlatformQuery();
    query.name = 'SourceTypeSelectedFieldKeysQuery';
    query.api = this.api;
    query.isCamelCase = true;
    this.fuseSplashScreenService.show();
    query.fieldValues['StageAreaSourceId'] = sourceTypeGroup.id;
    query.fieldValues['WorkbookId'] = this.workbookId;
    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response => {
      this.getSourceTypeDetails(this.sourceTypeGroup);
    });
  }

  // Field defintion of source types
  getSourceTypeDetails(stageAreaSource: any) {
    const platformQueryResponse$ = [];
    this.fuseSplashScreenService.show();
    stageAreaSource.sourceTypes.forEach((sourceTypeInfo, index) => {
      const query = new PlatformQuery();
      query.name = 'StageAreaFieldDefinitionQuery';
      query.api = sourceTypeInfo.api;
      query.isCamelCase = true;
      query.fieldValues['SourceType'] = sourceTypeInfo.sourceType;
      query.fieldValues['IsOwner'] = sourceTypeInfo.isOwner;
      query.fieldValues['PrimaryKey'] = sourceTypeInfo.primaryKey;
      const response$ = this.platformQueryService.executeQuery(query).pipe(take(1), map(response => ({index, response})));
      platformQueryResponse$.push(response$);
    });

    this.fieldKeys = {};
    concat(...platformQueryResponse$).subscribe((value : any) => {
        const response = value.response;
        let fieldDefinitions = response.fieldValues['FieldDefinitions'];
        if(fieldDefinitions) {
          fieldDefinitions = this.filterFieldDefinitions(fieldDefinitions);
          stageAreaSource.sourceTypes[value.index].isExpanded = true;
          stageAreaSource.sourceTypes[value.index].fieldDefinitions = fieldDefinitions;
        }
        this.fuseSplashScreenService.hide();
    });
  }
  filterFieldDefinitions(fieldDefinitions: any[]) {
    fieldDefinitions = fieldDefinitions.filter(fd => !this.fieldKeys[fd.key]);
    fieldDefinitions.forEach(field => {
      field.isSelected= field.frozen || this.selectedKeys.some(key => field.key == key);
      this.fieldKeys[field.key] = field.key;
    });
    return fieldDefinitions;
  }


  onSourceCheckBoxClicked($event: MatCheckboxChange, sourceType: any) {
    sourceType.fieldDefinitions.forEach(field => {
      if(field.frozen == false)
        field.isSelected = $event.checked;
    });
  }

  isAllSourceFieldSelected(sourceType: any) {
    return sourceType.fieldDefinitions.every(field => field.isSelected === true);
  }

  hasSourceFieldsIndeterminateState(sourceType: any) {
   const selectedCount = sourceType.fieldDefinitions.filter(field => field.isSelected).length;
    if(selectedCount > 0)
      return true;
    else
      return false;
  }
  onFieldChange($event: MatCheckboxChange, field: any, sourceType: any) {
    field.isSelected = $event.checked;
  }


  // Reference Type
  onAddReferenceType() {
    const referenceName = this.manageStageAreaForm.controls['referenceType'].value;
    this.addReferenceType(referenceName);
  }

  addReferenceType(referenceName: string) {
    const query = new PlatformQuery();
    query.name = 'CreateReferenceTypeQuery';
    query.api = 'SpreadsheetApi';
    query.isCamelCase = true;
    this.fuseSplashScreenService.show();
    query.fieldValues['ReferenceName'] = referenceName;
    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response => {
      this.fuseSplashScreenService.hide();
      const referenceType = response.fieldValues['ReferenceType'];
      this.referenceTypes.push(referenceType);
      this.manageStageAreaForm.get('referenceType').setValue(referenceType);

    });
  }
  getReferenceTypes() {
    const query = new PlatformQuery();
    query.name = 'ReferenceTypeQuery';
    query.api = 'SpreadsheetApi';
    query.isCamelCase = true;
    this.fuseSplashScreenService.show();
    query.fieldValues['WorksheetId'] = this.worksheetId;
    query.fieldValues['SelectedSourceMappingStageId'] =
    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response => {
      this.fuseSplashScreenService.hide();

    });
  }

  searchReferenceList(value: any) {
    if (this.referenceTypes) {
      const filter = value.toLowerCase();
      const filteredList = this.referenceTypes.filter(option => {
        console.log(option);
        return option.toLowerCase().indexOf(filter) === 0;
      });
      if (filteredList.length === 0) {
        this.possibleNewReference = true;
      } else {
        this.possibleNewReference = false;
      }
      this.filteredOptions = filteredList;
    }
  }

  // Dialog box Ok Clicked
  onOkClicked() {
    if(this.manageStageAreaForm.valid) {
      this.data.input.sourceTypeGroup = this.sourceTypeGroup;
      this.data.input.referenceType = this.manageStageAreaForm.controls['referenceType'].value;
      return true;
    }
    return false;
  }
}
